import React from "react";

import { Row, Col } from "antd";
import { Form, Formik } from "formik";

import { queryCache } from "react-query";
import * as Yup from "yup";

import { EMPLOYEE_QUERY_KEYS } from "manager/hooks/api/employeeQueries";
import { Input, Select, VeroFormField } from "shared/components/Form";
import { Modal } from "shared/components/Modal";
import Spacer from "shared/components/Spacer";
import { HTTP_ERROR_CODES, NOTIFICATIONS } from "shared/config/constants";
import useModal from "shared/hooks/useModal";
import { inviteEmployee } from "shared/lib/api/employeeApi";
import { ApiError, logError } from "shared/lib/errors";
import { openNotification } from "shared/utils/ui";

import AssignEmployeeToPropertiesModal from "../EmployeePropertiesModal";

import InviteEmployeeSuccessModal from "./InviteEmployeeSuccessModal";

const inviteSchema = Yup.object({
  firstName: Yup.string().trim().required("Required"),
  lastName: Yup.string().trim().required("Required"),
  email: Yup.string().trim().email("Enter a valid email").required("Required"),
  roleId: Yup.number().required("Required"),
});

export interface InviteEmployeeModalProps {
  roles: { id: number; name: string }[];
}

const FIELD = {
  firstName: "firstName",
  lastName: "lastName",
  email: "email",
  roleId: "roleId",
};

const InviteEmployeeModal = ({ roles }: InviteEmployeeModalProps) => {
  const { closeActiveModalDialog, openModalDialog } = useModal();

  const openAssignThenSuccess = (
    employee: {
      id: number;
      user: { firstName: string; lastName: string; email: string };
    },
    submittedEmail: string
  ) => {
    queryCache.invalidateQueries(EMPLOYEE_QUERY_KEYS.getEmployees());
    closeActiveModalDialog();
    openModalDialog(AssignEmployeeToPropertiesModal, {
      employeeId: employee.id,
      firstName: employee.user.firstName,
      lastName: employee.user.lastName,
      inviteFlow: true,
      onInviteAssignmentComplete: (assignment) => {
        openModalDialog(InviteEmployeeSuccessModal, {
          assignment,
          employeeName: `${employee.user.firstName} ${employee.user.lastName}`,
          email: submittedEmail,
        });
      },
    });
  };

  return (
    <Formik
      validationSchema={inviteSchema}
      initialValues={{
        firstName: "",
        lastName: "",
        email: "",
        roleId: roles[0]?.id as number | undefined,
      }}
      enableReinitialize
      onSubmit={async (values, { setFieldError, setSubmitting }) => {
        setSubmitting(true);
        try {
          const created = await inviteEmployee({
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            roleId: values.roleId,
          });
          openAssignThenSuccess(created, values.email.trim());
        } catch (err) {
          if (
            err instanceof ApiError &&
            err.statusCode === HTTP_ERROR_CODES.conflict
          ) {
            const msg =
              err.errors?.email?.[0] || "This email is already in use.";
            setFieldError(FIELD.email, msg);
          } else if (err instanceof ApiError) {
            const nfe = err.errors?.nonFieldErrors;
            const detail =
              Array.isArray(nfe) && nfe.length > 0
                ? nfe[0]
                : "We couldn't send the invite. Please try again.";
            openNotification(detail, NOTIFICATIONS.error);
          } else {
            logError(err);
            openNotification(
              "We couldn't send the invite. Please try again.",
              NOTIFICATIONS.error
            );
          }
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ submitForm, isSubmitting, isValid }) => (
        <Form>
          {/* @ts-ignore */}
          <Modal
            title="Invite employee"
            submitButtonLabel={isSubmitting ? "Sending..." : "Invite Employee"}
            submit={submitForm}
            closeOnSubmit={false}
            submitButtonDisabled={
              isSubmitting || !isValid || !roles || roles.length === 0
            }
            width="auto"
            style={{ maxWidth: 560 }}
          >
            <Modal.Body>
              {(!roles || roles.length === 0) && (
                <p>
                  No roles are configured for this company yet. Add a role
                  before inviting employees.
                </p>
              )}
              <Row gutter={[20, 20]}>
                <Col md={{ span: 12 }} xs={{ span: 24 }}>
                  {/* @ts-ignore */}
                  <VeroFormField
                    id={FIELD.firstName}
                    name={FIELD.firstName}
                    data-testid={FIELD.firstName}
                    label="First Name"
                    type="text"
                    required
                    as={Input}
                  />
                </Col>
                <Col md={{ span: 12 }} xs={{ span: 24 }}>
                  {/* @ts-ignore */}
                  <VeroFormField
                    id={FIELD.lastName}
                    name={FIELD.lastName}
                    data-testid={FIELD.lastName}
                    label="Last Name"
                    type="text"
                    required
                    as={Input}
                  />
                </Col>
              </Row>
              <Spacer />
              <Row gutter={[20, 20]}>
                <Col md={{ span: 24 }}>
                  {/* @ts-ignore */}
                  <VeroFormField
                    id={FIELD.email}
                    name={FIELD.email}
                    data-testid={FIELD.email}
                    label="Email"
                    type="email"
                    required
                    as={Input}
                  />
                </Col>
              </Row>
              <Spacer />
              <Row gutter={[20, 20]}>
                <Col md={{ span: 24 }}>
                  {/* @ts-ignore */}
                  <VeroFormField
                    as={Select}
                    name={FIELD.roleId}
                    id={FIELD.roleId}
                    label="Role"
                    required
                    onBlur={undefined}
                    optionFilterProp="label"
                  >
                    {roles?.map((role) => (
                      <Select.Option
                        key={role.id}
                        label={role.name}
                        value={role.id}
                      >
                        {role.name}
                      </Select.Option>
                    ))}
                  </VeroFormField>
                </Col>
              </Row>
            </Modal.Body>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default InviteEmployeeModal;
